
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as config from '../config'
import * as history from '../history'
import * as client from '../client'
import * as feed from '../feed'
import * as storage from '../storage'
import * as exposed from '../exposed'
import * as execution from '../execution'
import * as backup from '../backup'
import * as inner from '../inner'
import * as trade from '../trade'
import * as security from '../security'
import * as document from '../document'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {ConfigUpdateRequest} from './ConfigUpdateRequest';
import {FieldModelConfigUpdateRequest} from './ConfigUpdateRequest';
import {MarkupProfileModifyResponse} from './MarkupProfileModifyResponse';
import {MarkupValue} from '../core/MarkupValue';
import {FieldModelMarkupValue} from '../core/MarkupValue';

/**
 * MarkupProfileModifyRequest struct
 */
class MarkupProfileModifyRequest extends ConfigUpdateRequest {
  /**
   * Initialize struct
   * @param {!ConfigUpdateRequest=} parent
   * @param {UUID=} markupProfileId
   * @param {string=} markupProfileName
   * @param {string=} newName
   * @param {string=} newComment
   * @param {string=} newDescription
   * @param {!Array=} newValues
   * @param {!boolean=} valuesReset
   * @constructor
   */
  constructor (parent = new ConfigUpdateRequest(), markupProfileId = null, markupProfileName = null, newName = null, newComment = null, newDescription = null, newValues = [], valuesReset = false) {
    super()
    super.copy(parent)
    this.MarkupProfileId = markupProfileId
    this.MarkupProfileName = markupProfileName
    this.NewName = newName
    this.NewComment = newComment
    this.NewDescription = newDescription
    this.NewValues = newValues
    this.ValuesReset = valuesReset
  }

  /**
   * Copy struct (shallow copy)
   * @this {!MarkupProfileModifyRequest}
   * @param {!MarkupProfileModifyRequest} other Other struct
   * @returns {!MarkupProfileModifyRequest} This struct
   */
  copy (other) {
    super.copy(other)
    if (other.MarkupProfileId != null) {
      this.MarkupProfileId = new UUID(other.MarkupProfileId)
    } else {
      this.MarkupProfileId = null
    }
    if (other.MarkupProfileName != null) {
      this.MarkupProfileName = other.MarkupProfileName
    } else {
      this.MarkupProfileName = null
    }
    if (other.NewName != null) {
      this.NewName = other.NewName
    } else {
      this.NewName = null
    }
    if (other.NewComment != null) {
      this.NewComment = other.NewComment
    } else {
      this.NewComment = null
    }
    if (other.NewDescription != null) {
      this.NewDescription = other.NewDescription
    } else {
      this.NewDescription = null
    }
    if (other.NewValues != null) {
      this.NewValues = []
      for (let item of other.NewValues) {
        if (item != null) {
          let tempItem
          tempItem = MarkupValue.fromObject(item)
          this.NewValues.push(tempItem)
        } else {
          this.NewValues.push(null)
        }
      }
    } else {
      this.NewValues = null
    }
    if (other.ValuesReset != null) {
      this.ValuesReset = other.ValuesReset
    } else {
      this.ValuesReset = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!MarkupProfileModifyRequest}
   * @returns {!MarkupProfileModifyRequest} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new MarkupProfileModifyRequestModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new MarkupProfileModifyRequestModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!MarkupProfileModifyRequest}
   * @param {!MarkupProfileModifyRequest} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof MarkupProfileModifyRequest)) {
      throw new TypeError('Instance of MarkupProfileModifyRequest is required!')
    }
    // noinspection RedundantIfStatementJS
    if (!super.eq(other)) {
      return false
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!MarkupProfileModifyRequest}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    let parent = super.toJSON()
    let current = {
      MarkupProfileId: ((this.MarkupProfileId != null) ? this.MarkupProfileId.toString() : null),
      MarkupProfileName: ((this.MarkupProfileName != null) ? this.MarkupProfileName : null),
      NewName: ((this.NewName != null) ? this.NewName : null),
      NewComment: ((this.NewComment != null) ? this.NewComment : null),
      NewDescription: ((this.NewDescription != null) ? this.NewDescription : null),
      NewValues: ((this.NewValues != null) ? Array.from(this.NewValues, item => ((item != null) ? item : null)) : null),
      ValuesReset: ((this.ValuesReset != null) ? this.ValuesReset : null)
    }
    return { ...parent, ...current }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return MarkupProfileModifyRequest.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!MarkupProfileModifyRequest} other Object value
   * @returns {!MarkupProfileModifyRequest} Created struct
   */
  static fromObject (other) {
    return new MarkupProfileModifyRequest().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!MarkupProfileModifyRequest}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return MarkupProfileModifyRequest.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!MarkupProfileModifyRequest}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 6139
  }
}

export { MarkupProfileModifyRequest };

/**
 * Fast Binary Encoding MarkupProfileModifyRequest field model
 */
class FieldModelMarkupProfileModifyRequest extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this._parent = new FieldModelConfigUpdateRequest(buffer, 4 + 4)
    this.MarkupProfileId = new fbe.FieldModelOptional(new fbe.FieldModelUUID(buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4), buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4)
    this.MarkupProfileName = new fbe.FieldModelOptional(new fbe.FieldModelString(buffer, this.MarkupProfileId.fbeOffset + this.MarkupProfileId.fbeSize), buffer, this.MarkupProfileId.fbeOffset + this.MarkupProfileId.fbeSize)
    this.NewName = new fbe.FieldModelOptional(new fbe.FieldModelString(buffer, this.MarkupProfileName.fbeOffset + this.MarkupProfileName.fbeSize), buffer, this.MarkupProfileName.fbeOffset + this.MarkupProfileName.fbeSize)
    this.NewComment = new fbe.FieldModelOptional(new fbe.FieldModelString(buffer, this.NewName.fbeOffset + this.NewName.fbeSize), buffer, this.NewName.fbeOffset + this.NewName.fbeSize)
    this.NewDescription = new fbe.FieldModelOptional(new fbe.FieldModelString(buffer, this.NewComment.fbeOffset + this.NewComment.fbeSize), buffer, this.NewComment.fbeOffset + this.NewComment.fbeSize)
    this.NewValues = new fbe.FieldModelVector(new FieldModelMarkupValue(buffer, this.NewDescription.fbeOffset + this.NewDescription.fbeSize), buffer, this.NewDescription.fbeOffset + this.NewDescription.fbeSize)
    this.ValuesReset = new fbe.FieldModelBool(buffer, this.NewValues.fbeOffset + this.NewValues.fbeSize)
  }

  /**
   * Get the ConfigUpdateRequest field model
   * @this {!FieldModelMarkupProfileModifyRequest}
   * @returns {!FieldModelConfigUpdateRequest} ConfigUpdateRequest field model
   */
  get parent () {
    return this._parent
  }

  /**
   * Get the field size
   * @this {!FieldModelMarkupProfileModifyRequest}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelMarkupProfileModifyRequest}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.parent.fbeBody - 4 - 4 + this.MarkupProfileId.fbeSize + this.MarkupProfileName.fbeSize + this.NewName.fbeSize + this.NewComment.fbeSize + this.NewDescription.fbeSize + this.NewValues.fbeSize + this.ValuesReset.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelMarkupProfileModifyRequest}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.parent.fbeExtra + this.MarkupProfileId.fbeExtra + this.MarkupProfileName.fbeExtra + this.NewName.fbeExtra + this.NewComment.fbeExtra + this.NewDescription.fbeExtra + this.NewValues.fbeExtra + this.ValuesReset.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelMarkupProfileModifyRequest}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelMarkupProfileModifyRequest.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelMarkupProfileModifyRequest}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 6139
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelMarkupProfileModifyRequest}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelMarkupProfileModifyRequest}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) > fbeStructSize) {
      return true
    }
    if (!this.parent.verifyFields(fbeStructSize)) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.MarkupProfileId.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.MarkupProfileId.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.MarkupProfileId.fbeSize

    if ((fbeCurrentSize + this.MarkupProfileName.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.MarkupProfileName.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.MarkupProfileName.fbeSize

    if ((fbeCurrentSize + this.NewName.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.NewName.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.NewName.fbeSize

    if ((fbeCurrentSize + this.NewComment.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.NewComment.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.NewComment.fbeSize

    if ((fbeCurrentSize + this.NewDescription.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.NewDescription.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.NewDescription.fbeSize

    if ((fbeCurrentSize + this.NewValues.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.NewValues.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.NewValues.fbeSize

    if ((fbeCurrentSize + this.ValuesReset.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.ValuesReset.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.ValuesReset.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelMarkupProfileModifyRequest}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelMarkupProfileModifyRequest}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelMarkupProfileModifyRequest}
   * @param {!MarkupProfileModifyRequest} fbeValue Default value, defaults is new MarkupProfileModifyRequest()
   * @returns {!MarkupProfileModifyRequest} MarkupProfileModifyRequest value
   */
  get (fbeValue = new MarkupProfileModifyRequest()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelMarkupProfileModifyRequest}
   * @param {!MarkupProfileModifyRequest} fbeValue MarkupProfileModifyRequest value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) <= fbeStructSize) {
      this.parent.getFields(fbeValue, fbeStructSize)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.MarkupProfileId.fbeSize) <= fbeStructSize) {
      fbeValue.MarkupProfileId = this.MarkupProfileId.get()
    } else {
      fbeValue.MarkupProfileId = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.MarkupProfileId.fbeSize

    if ((fbeCurrentSize + this.MarkupProfileName.fbeSize) <= fbeStructSize) {
      fbeValue.MarkupProfileName = this.MarkupProfileName.get()
    } else {
      fbeValue.MarkupProfileName = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.MarkupProfileName.fbeSize

    if ((fbeCurrentSize + this.NewName.fbeSize) <= fbeStructSize) {
      fbeValue.NewName = this.NewName.get()
    } else {
      fbeValue.NewName = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.NewName.fbeSize

    if ((fbeCurrentSize + this.NewComment.fbeSize) <= fbeStructSize) {
      fbeValue.NewComment = this.NewComment.get()
    } else {
      fbeValue.NewComment = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.NewComment.fbeSize

    if ((fbeCurrentSize + this.NewDescription.fbeSize) <= fbeStructSize) {
      fbeValue.NewDescription = this.NewDescription.get()
    } else {
      fbeValue.NewDescription = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.NewDescription.fbeSize

    if ((fbeCurrentSize + this.NewValues.fbeSize) <= fbeStructSize) {
      this.NewValues.get(fbeValue.NewValues)
    } else {
      fbeValue.NewValues.length = 0
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.NewValues.fbeSize

    if ((fbeCurrentSize + this.ValuesReset.fbeSize) <= fbeStructSize) {
      fbeValue.ValuesReset = this.ValuesReset.get(false)
    } else {
      fbeValue.ValuesReset = false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.ValuesReset.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelMarkupProfileModifyRequest}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelMarkupProfileModifyRequest}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelMarkupProfileModifyRequest}
   * @param {!MarkupProfileModifyRequest} fbeValue MarkupProfileModifyRequest value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelMarkupProfileModifyRequest}
   * @param {MarkupProfileModifyRequest} fbeValue MarkupProfileModifyRequest value
   */
  setFields (fbeValue) {
    this.parent.setFields(fbeValue)
    this.MarkupProfileId.set(fbeValue.MarkupProfileId)
    this.MarkupProfileName.set(fbeValue.MarkupProfileName)
    this.NewName.set(fbeValue.NewName)
    this.NewComment.set(fbeValue.NewComment)
    this.NewDescription.set(fbeValue.NewDescription)
    this.NewValues.set(fbeValue.NewValues)
    this.ValuesReset.set(fbeValue.ValuesReset)
  }
}

export { FieldModelMarkupProfileModifyRequest };

/**
 * Fast Binary Encoding MarkupProfileModifyRequest model
 */
class MarkupProfileModifyRequestModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelMarkupProfileModifyRequest(this.buffer, 4)
  }

  /**
   * Get the MarkupProfileModifyRequest model
   * @this {!MarkupProfileModifyRequestModel}
   * @returns {!FieldModelMarkupProfileModifyRequest} model MarkupProfileModifyRequest model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!MarkupProfileModifyRequestModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!MarkupProfileModifyRequestModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return MarkupProfileModifyRequestModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!MarkupProfileModifyRequestModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelMarkupProfileModifyRequest.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!MarkupProfileModifyRequestModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!MarkupProfileModifyRequestModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!MarkupProfileModifyRequestModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!MarkupProfileModifyRequestModel}
   * @param {!MarkupProfileModifyRequest} value MarkupProfileModifyRequest value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!MarkupProfileModifyRequestModel}
   * @param {!MarkupProfileModifyRequest} value MarkupProfileModifyRequest value, defaults is new MarkupProfileModifyRequest()
   * @return {!object} Deserialized MarkupProfileModifyRequest value and its size
   */
  deserialize (value = new MarkupProfileModifyRequest()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new MarkupProfileModifyRequest(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new MarkupProfileModifyRequest(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!MarkupProfileModifyRequestModel}
   * @param {!number} prev Previous MarkupProfileModifyRequest model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { MarkupProfileModifyRequestModel };
MarkupProfileModifyRequest.__has_response = true;
MarkupProfileModifyRequest.__response_class = MarkupProfileModifyResponse;
